import { Handle, Position } from "@xyflow/react";
import React from "react";
import { Header } from "build-ts/metadata";
import { ColumnType, MetadataTypeEnum } from "build-ts/enums";
import { Icon } from "@chakra-ui/react";
import { AppIcons } from "constants/icons";

interface CustomNodeData {
  header: Header;
  style: React.CSSProperties;
  /** Set by the visualizer to dim unmatched search results */
  searchDimmed?: boolean;
  /** Set by the visualizer to highlight search matches */
  searchHighlighted?: boolean;
}

interface ICustomNodeProps {
  data: CustomNodeData;
  selected?: boolean;
}

const NODE_DIAMETER = 72;

export const findNodeColor = (header: Header): string => {
  if (!header) return "#a0aec0";
  if (header.type === MetadataTypeEnum.METRIC) return "#6b21a8";
  if (header.type === MetadataTypeEnum.BENCHMARK) return "#c2410c";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.ATTRIBUTE) return "#1d4ed8";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.MEASURE) return "#15803d";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.TEMPORAL) return "#a16207";
  return "#6b7280";
};

export const findNodeLabel = (header: Header): string => {
  if (!header) return "Unknown";
  if (header.type === MetadataTypeEnum.METRIC) return "Metric";
  if (header.type === MetadataTypeEnum.BENCHMARK) return "Benchmark";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.ATTRIBUTE) return "Attribute";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.MEASURE) return "Measure";
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.TEMPORAL) return "Time";
  return "Node";
};

const getNodeIcon = (header: Header) => {
  if (!header) return null;
  if (header.type === MetadataTypeEnum.METRIC) return <Icon as={AppIcons.metric} boxSize={8} color="white" />;
  if (header.type === MetadataTypeEnum.BENCHMARK) return <Icon as={AppIcons.benchmark} boxSize={8} color="white" />;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.ATTRIBUTE)
    return <Icon as={AppIcons.attribute} boxSize={7} color="white" />;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.MEASURE)
    return <Icon as={AppIcons.measure} boxSize={8} color="white" />;
  if (header.type === MetadataTypeEnum.KG_COLUMN && header.column_type === ColumnType.TEMPORAL)
    return <Icon as={AppIcons.timeframe} boxSize={7} color="white" />;
  return null;
};

const KnowledgeGraphNode: React.FC<ICustomNodeProps> = ({ data, selected }) => {
  const color = findNodeColor(data.header);
  const { searchDimmed, searchHighlighted } = data;

  const ringStyle: React.CSSProperties = selected
    ? {
        boxShadow: `0 0 0 4px ${color}55, 0 0 0 8px ${color}22`,
        transform: "scale(1.08)",
      }
    : searchHighlighted
      ? {
          boxShadow: "0 0 0 4px #f59e0b, 0 0 0 8px #f59e0b44",
          transform: "scale(1.08)",
        }
      : {
          boxShadow: `0 4px 14px ${color}44`,
        };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        opacity: searchDimmed ? 0.2 : 1,
        transition: "opacity 0.25s ease, transform 0.2s ease",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {/* Circle */}
      <div
        style={{
          width: NODE_DIAMETER,
          height: NODE_DIAMETER,
          borderRadius: "50%",
          background: `linear-gradient(135deg, ${color}dd, ${color})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          ...ringStyle,
        }}
      >
        {getNodeIcon(data.header)}
      </div>

      {/* Label below circle */}
      <div
        style={{
          maxWidth: 160,
          textAlign: "center",
        }}
      >
        {/* Entity name — big and prominent */}
        <div
          style={{
            fontSize: "1.1rem",
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.25,
            marginBottom: 3,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {data?.header?.name || "No Label"}
        </div>
        {/* Type badge — secondary, below */}
        <div
          style={{
            fontSize: "0.65rem",
            fontWeight: 600,
            color,
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            lineHeight: 1.2,
            opacity: 0.85,
          }}
        >
          {findNodeLabel(data.header)}
        </div>
      </div>
      {/* Handle is a component from React Flow that defines the connection points on a node 
        — the spots where edges (lines/arrows) attach when connecting nodes in the graph.
      */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: "transparent", border: "none", top: NODE_DIAMETER / 2 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: "transparent", border: "none", top: NODE_DIAMETER / 2 }}
      />
    </div>
  );
};

export default KnowledgeGraphNode;
